import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { httpsCallable } from "firebase/functions";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { Input } from "./Form";
import { useToast } from "./Toast";
import { functions } from "../../firebase";
import { AD_LIBRARY_SEARCH_URL, adLibraryUrl, parseAdLibraryId } from "../../utils/metaAds";

// Matches the shared link Button beside it (Button.scss .btn--link, small).
const adLinkStyle = {
  color: "var(--tool-color, #8b5cf6)", fontWeight: 600, fontSize: "0.875rem", textDecoration: "none",
};

/**
 * CampaignNamesModal — give a readable name to a campaign that arrived as a
 * bare id, and record when two values are one campaign.
 *
 * Why this is CONFIGURATION and not part of a results view: naming a campaign
 * changes what the whole team believes a bar means. It is admin-gated
 * server-side (setCampaignLabelCallable), and this screen is only ever reached
 * deliberately — never a mode toggled onto a chart someone was reading.
 *
 * ⚠️ Names are a DISPLAY layer. Nothing written here touches qr_scans or a
 * rollup. Writing a name into the data is how /linkedin ended up permanently
 * filed under "promoter-launch" — true when the alias was made, wrong months
 * later.
 */
const CampaignNamesModal = ({ isOpen, onClose, onSaved }) => {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [builtAt, setBuiltAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [showAll, setShowAll] = useState(false);
  // The ad behind a Meta campaign: which row's link is being edited, and what was pasted.
  const [adEditing, setAdEditing] = useState(null);
  const [adDraft, setAdDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functions, "listCampaignsNeedingNamesCallable");
      const res = await fn({});
      setRows(res.data?.rows || []);
      setBuiltAt(res.data?.builtAt || null);
    } catch (error) {
      toast.error(error.message || "Could not load campaigns", {
        debugInfo: {
          operation: "listCampaignsNeedingNamesCallable",
          error: error.stack || error.toString(),
          context: "CampaignNamesModal load",
        },
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (isOpen) load(); }, [isOpen, load]);

  const save = async (row) => {
    const label = (drafts[row.key] ?? row.label ?? "").trim();
    setSaving(row.key);
    try {
      const fn = httpsCallable(functions, "setCampaignLabelCallable");
      await fn({ key: row.key, label });
      toast.success(label ? `Named "${label}"` : "Name cleared");
      if (onSaved) onSaved({ key: row.key, label });
      setRows((prev) => prev.map((r) => (r.key === row.key
        ? { ...r, label: label || null, kind: label ? "named" : "opaque" } : r)));
      setDrafts((d) => { const n = { ...d }; delete n[row.key]; return n; });
    } catch (error) {
      toast.error(error.message || "Could not save the name", {
        debugInfo: {
          operation: "setCampaignLabelCallable",
          params: { key: row.key, label },
          error: error.stack || error.toString(),
        },
      });
    } finally {
      setSaving(null);
    }
  };

  // Saves only the ad link. The server leaves the name alone when a request carries no name.
  const saveAd = async (row) => {
    const pasted = adDraft.trim();
    const adLibraryId = pasted ? parseAdLibraryId(pasted) : "";
    if (pasted && !adLibraryId) {
      toast.warning("Paste the ad's link from Meta's Ad Library, or its Library ID.");
      return;
    }
    setSaving(row.key);
    try {
      const fn = httpsCallable(functions, "setCampaignLabelCallable");
      await fn({ key: row.key, adLibraryId });
      toast.success(adLibraryId ? "Ad link saved" : "Ad link removed");
      setRows((prev) => prev.map((r) => (r.key === row.key
        ? { ...r, adLibraryId: adLibraryId || null } : r)));
      setAdEditing(null);
      setAdDraft("");
    } catch (error) {
      toast.error(error.message || "Could not save the ad link", {
        debugInfo: {
          operation: "setCampaignLabelCallable",
          params: { key: row.key, adLibraryId },
          error: error.stack || error.toString(),
          context: "CampaignNamesModal saveAd",
        },
      });
    } finally {
      setSaving(null);
    }
  };

  // Unnamed ids first — they are the only rows that need anything.
  const needing = rows.filter((r) => r.kind === "opaque");
  const named = rows.filter((r) => r.kind === "named");
  const shown = showAll ? rows : [...needing, ...named];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Campaign names" size="large">
      <p style={{ margin: "0 0 6px", fontSize: 14, color: "#4b5563" }}>
        Meta sends ad campaigns as bare ids. Naming one changes how it reads
        everywhere in Analytics — the id is kept, because it is what reconciles
        back to Ads Manager. Each one is a single ad, and Meta's public Ad Library
        shows it: "View ad" opens it once its link has been added.
      </p>
      <p style={{ margin: "0 0 18px", fontSize: 12, color: "#9ca3af" }}>
        {needing.length} unnamed · {named.length} named
        {builtAt && ` · inventory last rebuilt ${String(builtAt).slice(0, 10)}`}
        {builtAt && " (run scripts/campaign-rollup-shadow.js to refresh)"}
      </p>

      {loading && <p style={{ fontSize: 13 }}>Loading…</p>}

      {!loading && shown.map((r) => (
        <div
          key={r.key}
          role="group"
          aria-label={`Campaign ${r.raw}`}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) 240px auto",
            gap: 10,
            alignItems: "center",
            padding: "10px 0",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{
              fontFamily: "monospace", fontSize: 12,
              color: r.kind === "opaque" ? "#6b7280" : "#111827",
              overflowWrap: "anywhere",
            }}>
              {r.raw}
            </div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              {r.scans.toLocaleString()} redirects
              {r.aliases?.length ? ` · via /${r.aliases.join(", /")}` : ""}
              {r.first && r.last && ` · ${r.first} → ${r.last}`}
            </div>
            {/* The ad itself. Only the server marks a row as a Meta ad, so against an older
                backend nothing is offered — an ad-link save there would clear the name. */}
            {r.metaAd && (
              <div style={{ marginTop: 2, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                {adLibraryUrl(r.adLibraryId) ? (
                  <a href={adLibraryUrl(r.adLibraryId)} target="_blank" rel="noopener noreferrer" style={adLinkStyle}>
                    View ad ↗
                  </a>
                ) : (
                  <a
                    href={AD_LIBRARY_SEARCH_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={adLinkStyle}
                    title="Opens Meta's Ad Library with every ClozeSure ad"
                  >
                    Find this ad ↗
                  </a>
                )}
                <Button
                  variant="link"
                  size="small"
                  onClick={() => {
                    setAdEditing(adEditing === r.key ? null : r.key);
                    setAdDraft("");
                  }}
                >
                  {adEditing === r.key ? "Cancel" : r.adLibraryId ? "Change link" : "Add its link"}
                </Button>
              </div>
            )}
            {r.metaAd && adEditing === r.key && (
              <div style={{ marginTop: 6 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <Input
                    value={adDraft}
                    onChange={(e) => setAdDraft(e.target.value)}
                    placeholder="Ad Library link or Library ID"
                    aria-label={`Ad Library link for ${r.label || r.raw}`}
                    fullWidth
                  />
                  <Button
                    size="small"
                    variant="primary"
                    disabled={saving === r.key || (!adDraft.trim() && !r.adLibraryId)}
                    onClick={() => saveAd(r)}
                  >
                    {saving === r.key ? "Saving…" : adDraft.trim() || !r.adLibraryId ? "Save link" : "Remove link"}
                  </Button>
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                  In the Ad Library, the ad for this campaign is the one that started
                  {r.first ? ` on ${r.first}` : " on the day of its first redirect"}. Copy its link or
                  Library ID. Cards marked "2 ads use this creative" hide a second ad under
                  "See summary details".
                </div>
              </div>
            )}
          </div>
          <Input
            value={drafts[r.key] ?? r.label ?? ""}
            onChange={(e) => setDrafts((d) => ({ ...d, [r.key]: e.target.value }))}
            placeholder={r.kind === "opaque" ? "Name this campaign…" : ""}
          />
          <Button
            size="small"
            variant={drafts[r.key] !== undefined ? "primary" : "secondary"}
            disabled={saving === r.key || drafts[r.key] === undefined}
            onClick={() => save(r)}
          >
            {saving === r.key ? "Saving…" : "Save"}
          </Button>
        </div>
      ))}

      {!loading && !showAll && rows.length > shown.length && (
        <Button variant="link" size="small" onClick={() => setShowAll(true)}>
          Show all {rows.length} campaigns
        </Button>
      )}
      {!loading && !rows.length && (
        <p style={{ fontSize: 13, color: "#6b7280" }}>
          No campaign inventory yet — run <code>scripts/campaign-rollup-shadow.js</code> to build one.
        </p>
      )}
    </Modal>
  );
};

CampaignNamesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Called after each successful save — lets a results view refresh only when a name changed. */
  onSaved: PropTypes.func,
};

export default CampaignNamesModal;
export { CampaignNamesModal };
